import { useLayoutEffect, useRef, useState } from "react";
import {
	FlatRowInput,
	type FlatRowInputProps,
} from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { groupDigits } from "web_myplann/Shared/Utils/groupDigits";
import { parseThousandsFloat } from "web_myplann/Shared/Utils/numberFormatters";

const NBSP = " ";
const ALLOWED_FLOAT_CHARS_PATTERN = /[^\d.,]/g;
const NON_DIGIT_PATTERN = /\D/g;
const COMMA_PATTERN = /,/g;
const DEFAULT_DECIMAL_PLACES = 2;

const formatFloatForDisplay = (value: unknown): string => {
	if (value == null || value === "") return "";
	const numeric = typeof value === "number" ? value : Number(value);
	if (Number.isNaN(numeric)) return "";
	const [intPart, fracPart] = String(numeric).split(".");
	const intFormatted = groupDigits(intPart, NBSP);
	return fracPart ? `${intFormatted},${fracPart}` : intFormatted;
};

const formatFloatDraft = (raw: string, decimalPlaces: number): string => {
	const cleaned = raw
		.replace(ALLOWED_FLOAT_CHARS_PATTERN, "")
		.replace(".", ",");
	const sep = cleaned.indexOf(",");
	const intPart = sep === -1 ? cleaned : cleaned.slice(0, sep);
	const rawFracPart =
		sep === -1 ? null : cleaned.slice(sep + 1).replace(COMMA_PATTERN, "");
	const fracPart =
		rawFracPart === null ? null : rawFracPart.slice(0, decimalPlaces);
	const intDigits = intPart.replace(NON_DIGIT_PATTERN, "");
	const intGrouped = groupDigits(intDigits, NBSP);
	if (fracPart === null) return intGrouped;
	if (decimalPlaces === 0) return intGrouped;
	return `${intGrouped},${fracPart}`;
};

const ALLOWED_FLOAT_CHAR_PATTERN = /[\d.,]/;

const countAllowedCharsBefore = (raw: string, caret: number): number => {
	let count = 0;
	for (let i = 0; i < caret && i < raw.length; i++) {
		if (ALLOWED_FLOAT_CHAR_PATTERN.test(raw[i])) count++;
	}
	return count;
};

const caretPositionAfterNAllowedChars = (
	formatted: string,
	n: number,
): number => {
	if (n <= 0) return 0;
	let seen = 0;
	for (let i = 0; i < formatted.length; i++) {
		if (ALLOWED_FLOAT_CHAR_PATTERN.test(formatted[i])) {
			seen++;
			if (seen === n) return i + 1;
		}
	}
	return formatted.length;
};

type FloatFlatRowInputProps = Omit<FlatRowInputProps, "value" | "onChange"> & {
	value: unknown;
	onChange: (next: number | null) => void;
	decimalPlaces?: number;
	max?: number;
};

export const FloatFlatRowInput = ({
	value,
	onChange,
	onBlur,
	decimalPlaces = DEFAULT_DECIMAL_PLACES,
	max,
	...rest
}: FloatFlatRowInputProps) => {
	const [draft, setDraft] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const pendingCaretRef = useRef<number | null>(null);
	const displayValue = draft ?? formatFloatForDisplay(value);

	const handleChange = (next: string) => {
		const caret = inputRef.current?.selectionStart ?? next.length;
		const allowedBeforeCaret = countAllowedCharsBefore(next, caret);
		const formatted = formatFloatDraft(next, decimalPlaces);
		const parsed = parseThousandsFloat(formatted);
		if (max != null && parsed != null && parsed > max) return;
		pendingCaretRef.current = caretPositionAfterNAllowedChars(
			formatted,
			allowedBeforeCaret,
		);
		setDraft(formatted);
		onChange(parsed ?? null);
	};

	useLayoutEffect(() => {
		const pos = pendingCaretRef.current;
		if (pos == null || inputRef.current == null) return;
		inputRef.current.setSelectionRange(pos, pos);
		pendingCaretRef.current = null;
	}, [displayValue]);

	const handleBlur = () => {
		setDraft(null);
		onBlur?.();
	};

	return (
		<FlatRowInput
			{...rest}
			value={displayValue}
			onChange={handleChange}
			onBlur={handleBlur}
			inputRef={inputRef}
		/>
	);
};
